import { FileSearchOutlined } from '@ant-design/icons';
import { Popover, TableProps, Tag } from 'antd';
import { isArray } from 'lodash';
import EllipsisTooltip from 'src/app/components/EllipsisTooltipComponent/EllipsisTooltip';
import {
  convertUserEventsToTableData,
  getDetailsMessage,
} from 'src/app/components/TableComponent/LogsTableComponent/logs-table.config';
import 'src/app/components/TableComponent/LogsTableComponent/logs-table.scss';
import TableComponent, { CustomColumnsType } from 'src/app/components/TableComponent/TableComponent';
import { timeHelper } from 'src/app/helpers';
import { getEnumFilterOptions } from 'src/app/helpers/enum.helper';
import useGetTableData from 'src/app/hooks/useGetTableData';
import { useGetUsersQuery } from 'src/app/redux/user/userSlice';
import { UserAction } from 'src/enums/logger-enum';
import { Log } from 'src/types/log.type';
import { UserEventsColumnType } from 'src/types/table.type';
import { TableComponents } from 'virtualizedtableforantd4/dist/cjs/vt';

interface ILogsTableProps {
  dataSource: Log[];
  tableHeight: number | string;
  vt?: TableComponents;
  onTableChange?: TableProps['onChange'];
  filtersEnabled?: boolean;
  onTableReset?: () => void;
}
const LogsTable: React.FC<ILogsTableProps> = ({
  dataSource,
  tableHeight,
  vt,
  onTableChange,
  filtersEnabled,
  onTableReset,
}) => {
  const { tableDataSource } = useGetTableData({ convertFn: convertUserEventsToTableData, data: dataSource });

  const { data: users } = useGetUsersQuery();

  const renderDetails = (details: any, action: keyof typeof UserAction) => {
    switch (action) {
      case 'Update':
        return Object.entries(details).map(([key, value]: any) => {
          return (
            <div
              key={key}
              style={{
                display: 'flex',
                gap: '5px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              <span style={{ textTransform: 'capitalize', fontWeight: 'bold' }}>{key}:</span>
              <span style={{ textDecoration: 'line-through' }}>
                {isArray(value.old) ? value.old.join(', ') : value.old}
              </span>
              {'-->'}
              <span>{isArray(value.new) ? value.new.join(', ') : value.new}</span>
            </div>
          );
        });
      case 'Upload':
        return details.filenames.map((fn: any, idx: number) => {
          return <div key={idx}>{fn}</div>;
        });
      case 'NSearch':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', rowGap: '.5rem' }}>
            {details.dataSourceCategory ? (
              <div>
                <div style={{ fontWeight: 'bold' }}>Data Source Category</div>
                <div> {details.dataSourceCategory?.join(', ')}</div>
              </div>
            ) : null}
            {details.dataSourceType ? (
              <div>
                <div style={{ fontWeight: 'bold' }}>Data Source Type</div>
                <div> {details.dataSourceType?.join(', ')}</div>
              </div>
            ) : null}
            {details.dataSource ? (
              <div>
                <div style={{ fontWeight: 'bold' }}>Data Sources</div>
                <div> {details.dataSource?.join(', ')}</div>
              </div>
            ) : null}
            {details.interests ? (
              <div>
                <div style={{ fontWeight: 'bold' }}>Interests</div>
                <div>
                  {details.interests.map((int: string, idx: number) => {
                    return <div key={idx}>{int}</div>;
                  })}
                </div>
              </div>
            ) : null}
            {details.mediaTypes ? (
              <div>
                <div style={{ fontWeight: 'bold' }}>Media Types</div>
                <div>{details.mediaTypes?.join(', ')}</div>
              </div>
            ) : null}
            {details.startTime ? (
              <div>
                <div style={{ fontWeight: 'bold' }}>Start Date</div>
                <div>{timeHelper.formatDate(details.startTime)}</div>
              </div>
            ) : null}
            {details.endTime ? (
              <div>
                <div style={{ fontWeight: 'bold' }}>End Date</div>
                <div>{timeHelper.formatDate(details.endTime)}</div>
              </div>
            ) : null}
            {details.coordinates ? (
              <div>
                <div style={{ fontWeight: 'bold' }}>Coordinates</div>
                <div>
                  {details.coordinates.map((coordinate: { latitude: number; longitude: number }, idx: number) => {
                    return (
                      <div style={{ display: 'flex', columnGap: '0.5rem' }} key={idx}>
                        <span>{Number(coordinate.latitude.toFixed(2))}</span>
                        <span>{Number(coordinate.longitude.toFixed(2))}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        );
      default:
        return null;
    }
  };

  const columns: CustomColumnsType<UserEventsColumnType> = [
    {
      dataIndex: 'actor',
      title: 'User',
      width: '25%',
      sorter: filtersEnabled ? true : undefined,
      filters: filtersEnabled ? users?.rows.map((usr) => ({ text: usr.fullname, value: usr.fullname })) : undefined,
    },
    {
      dataIndex: 'action',
      title: 'Action',
      width: '15%',
      sorter: filtersEnabled ? true : undefined,
      filters: filtersEnabled
        ? getEnumFilterOptions(UserAction).map((obj) => ({
            text: obj.text,
            value: UserAction[obj.value as keyof typeof UserAction],
          }))
        : undefined,
      render: (action) => {
        const colorPalette: { [key in keyof typeof UserAction]: string } = {
          Create: '#568259',
          Update: '#263D42',
          Delete: '#8B1739',
          Upload: '#263D42',
          NSearch: '#568259',
        };
        const color = colorPalette[action as keyof typeof UserAction] || 'gray';
        return (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Tag bordered style={{ borderColor: color, color, fontWeight: 'bold', borderWidth: '1px' }}>
              {action.toUpperCase()}
            </Tag>
          </div>
        );
      },
    },
    {
      title: 'Details',
      dataIndex: 'details',
      width: '35%',
      render: (_, record) => {
        return (
          <div style={{ display: 'flex', gap: '5px' }}>
            <EllipsisTooltip> {getDetailsMessage(record)} </EllipsisTooltip>
            {record.details ? (
              <Popover
                getTooltipContainer={() =>
                  (document.getElementById('dashboard-full-screen') as HTMLElement) || document.body
                }
                overlayClassName="log-details-popover"
                content={renderDetails(record.details, record.action as keyof typeof UserAction)}
              >
                <FileSearchOutlined />
              </Popover>
            ) : null}
          </div>
        );
      },
    },
    {
      dataIndex: 'timestamp',
      title: 'Timestamp',
      width: '25%',
    },
  ];

  return (
    <TableComponent
      onTableReset={onTableReset}
      onChange={onTableChange}
      components={vt}
      columns={columns}
      dataSource={tableDataSource}
      scroll={{ y: tableHeight }}
    />
  );
};

export default LogsTable;
